/**
 * Off-screen render target for the "Export operation" feature.
 *
 * Composes, side by side:
 *   [ Bounded SVG map (active hexagons only, region labels, operation icons + arrows) ]
 *   [ Static operation-plan summary panel (war number, title, date, objective, priorities) ]
 */

import type { CSSProperties } from 'react';
import type { IconCategory, MapArrow, MapObject, OperationPlanDTO } from '@warplan/shared';
import {
  ATLAS,
  HEX_VERTICES,
  REGION_LABELS,
  TILE_W,
  TILE_H,
  findRegionLabel,
  tileAssetFor,
  type MapBackgroundKind,
} from '../lib/foxhole-maps.js';
import { getIconEntry, ICON_CATALOG } from '../lib/icon-catalog.js';
import { PRIORITY_SLOTS, ATTACK_SLOT_COUNT, DEFEND_SLOT_OFFSET, DEFEND_SLOT_COUNT } from '../lib/priority-sync.js';
import { ExportArrows } from './ExportArrows.js';
import { resolveIconCollisions } from '../lib/resolve-icon-collisions.js';
import {
  EXPORT_MAP_MAX_HEIGHT_PX,
  EXPORT_MAP_WIDTH_PX,
  EXPORT_PANEL_WIDTH_PX,
  type ExportBoundingBox,
} from '../lib/war-plan-export.js';

export interface ExportWarLabel {
  wx: number;
  wy: number;
  text: string;
  kind: 'major' | 'minor';
}

export interface OperationExportViewProps {
  bbox: ExportBoundingBox;
  markers: ReadonlyArray<MapObject>;
  collisionContext?: ReadonlyArray<MapObject>;
  arrows: ReadonlyArray<MapArrow>;
  disabledRegionKeys: ReadonlySet<string>;
  mapBackground: MapBackgroundKind;
  operationPlan: OperationPlanDTO | null;
  warLabels: ReadonlyArray<ExportWarLabel>;
  caption?: string;
}

const REGION_LABEL_FONT_SIZE = 38;
const MARKER_ICON_SIZE = 56;
const LIST_ICON_SIZE = 22;

/** Attribution shown on exported images when the 'ink' background mod is active. */
const INK_CREDIT = 'Fond de carte « Ink & Frontline » par Arkhitecton';

/**
 * Operation exports usually focus on a single hex (or two), which makes the
 * fixed world-space sizes for markers and region labels appear oversized
 * relative to the planner view. Scale them down when the bounding box covers
 * fewer than ~3 hexes, with a floor so they remain readable.
 *
 * `TILE_W` is the world-space width of one hex (~1024 units).
 */
function computeExportSizeScale(bbox: ExportBoundingBox): number {
  const spanInHexes = Math.min(bbox.width, bbox.height) / TILE_W;
  // Linear ramp: 0.45 at 1 hex → 1.0 at 3 hexes, clamped on both ends.
  const raw = 0.45 + (spanInHexes - 1) * (0.55 / 2);
  return Math.min(1, Math.max(0.45, raw));
}

/**
 * Operation sub-sections, in the canonical display order
 * (INFO, ATTACK, DEFEND, then the rest alphabetically).
 */
const OPERATION_SECTIONS: ReadonlyArray<IconCategory> = [
  'op_info',
  'op_arty',
  'op_build',
  'op_navy',
  'op_unit',
  'op_vehicle',
];

function findNearestLabel(
  wx: number,
  wy: number,
  warLabels: ReadonlyArray<ExportWarLabel>,
  kind: 'major' | 'minor',
): string | null {
  let best: ExportWarLabel | null = null;
  let bestDist = Infinity;
  for (const lbl of warLabels) {
    if (lbl.kind !== kind) continue;
    const dx = lbl.wx - wx;
    const dy = lbl.wy - wy;
    const d = dx * dx + dy * dy;
    if (d < bestDist) {
      bestDist = d;
      best = lbl;
    }
  }
  return best?.text ?? null;
}

const hexPoints = HEX_VERTICES.map((p) => `${p.x},${p.y}`).join(' ');

export function OperationExportView({
  bbox,
  markers,
  collisionContext,
  arrows,
  disabledRegionKeys,
  mapBackground,
  operationPlan,
  warLabels,
  caption,
}: OperationExportViewProps): React.JSX.Element {
  const aspect = bbox.width / bbox.height;
  let mapWidth = EXPORT_MAP_WIDTH_PX;
  let mapHeight = mapWidth / aspect;
  if (mapHeight > EXPORT_MAP_MAX_HEIGHT_PX) {
    mapHeight = EXPORT_MAP_MAX_HEIGHT_PX;
    mapWidth = mapHeight * aspect;
  }

  const sizeScale = computeExportSizeScale(bbox);
  const markerIconSize = MARKER_ICON_SIZE * sizeScale;
  const regionLabelFontSize = REGION_LABEL_FONT_SIZE * sizeScale;
  const regionLabelStrokeWidth = 4 * sizeScale;

  const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 16,
    padding: 16,
    background: '#0f172a',
    color: '#f1f5f9',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    width: mapWidth + EXPORT_PANEL_WIDTH_PX + 32 + 16,
  };

  const mapColumnStyle: CSSProperties = {
    position: 'relative',
    width: mapWidth,
    height: mapHeight,
    background: '#020617',
    borderRadius: 6,
    overflow: 'hidden',
  };

  const captionStyle: CSSProperties = {
    position: 'absolute',
    bottom: 6,
    right: 8,
    fontSize: 12,
    color: '#cbd5e1',
    textShadow: '0 0 4px rgba(0,0,0,0.9)',
  };

  const inkCreditStyle: CSSProperties = {
    position: 'absolute',
    bottom: 6,
    left: 8,
    fontSize: 10,
    color: '#94a3b8',
    textShadow: '0 0 4px rgba(0,0,0,0.9)',
  };

  return (
    <div style={containerStyle}>
      <div style={mapColumnStyle}>
        <svg
          width={mapWidth}
          height={mapHeight}
          viewBox={`${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block', background: '#020617' }}
        >
          <g>
            {ATLAS.map((tile) => {
              if (disabledRegionKeys.has(tile.key)) return null;
              return (
                <image
                  key={tile.key}
                  href={tileAssetFor(tile, mapBackground)}
                  x={tile.x - TILE_W / 2}
                  y={tile.y - TILE_H / 2}
                  width={TILE_W}
                  height={TILE_H}
                  preserveAspectRatio="none"
                />
              );
            })}
          </g>

          <g pointerEvents="none">
            {ATLAS.map((tile) => {
              if (disabledRegionKeys.has(tile.key)) return null;
              return (
                <polygon
                  key={`outline-${tile.key}`}
                  points={hexPoints}
                  transform={`translate(${tile.x} ${tile.y})`}
                  fill="none"
                  stroke="#94a3b8"
                  strokeOpacity={0.45}
                  strokeWidth={2}
                />
              );
            })}
          </g>

          <g pointerEvents="none">
            {(() => {
              const contextSource = collisionContext ?? markers;
              const sortedContext = [...contextSource].sort(
                (a, b) =>
                  new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
              );
              const rects = sortedContext.map((m) => ({
                id: m.id,
                x: m.position.x,
                y: m.position.y,
                scale: m.metadata.scale ?? 1,
                size: markerIconSize * (m.metadata.scale ?? 1),
              }));
              const adjusted = resolveIconCollisions(rects);
              const renderOrder = [...markers].sort(
                (a, b) =>
                  new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
              );
              return renderOrder.map((m) => {
                const entry = getIconEntry(m.kind, m.metadata.iconId);
                const size = markerIconSize * (m.metadata.scale ?? 1);
                const pos = adjusted.get(m.id) ?? m.position;
                return (
                  <image
                    key={m.id}
                    href={entry.src}
                    x={pos.x - size / 2}
                    y={pos.y - size / 2}
                    width={size}
                    height={size}
                    preserveAspectRatio="xMidYMid meet"
                  />
                );
              });
            })()}
          </g>

          <ExportArrows arrows={arrows} sizeScale={sizeScale} />

          <g pointerEvents="none">
            {ATLAS.map((tile) => {
              if (disabledRegionKeys.has(tile.key)) return null;
              return (
                <text
                  key={`label-${tile.key}`}
                  x={tile.x}
                  y={tile.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={regionLabelFontSize}
                  fontFamily="sans-serif"
                  fontWeight={700}
                  fill="#ffffff"
                  stroke="#0f172a"
                  strokeWidth={regionLabelStrokeWidth}
                  style={{ paintOrder: 'stroke fill' }}
                >
                  {REGION_LABELS[tile.key] ?? tile.key}
                </text>
              );
            })}
          </g>
        </svg>

        {mapBackground === 'ink' ? <div style={inkCreditStyle}>{INK_CREDIT}</div> : null}
        {caption ? <div style={captionStyle}>{caption}</div> : null}
      </div>

      <OperationPanel
        operationPlan={operationPlan}
        markers={markers}
        warLabels={warLabels}
      />
    </div>
  );
}

interface OperationPanelProps {
  operationPlan: OperationPlanDTO | null;
  markers: ReadonlyArray<MapObject>;
  warLabels: ReadonlyArray<ExportWarLabel>;
}

function OperationPanel({
  operationPlan,
  markers,
  warLabels,
}: OperationPanelProps): React.JSX.Element {
  const wrapper: CSSProperties = {
    width: EXPORT_PANEL_WIDTH_PX,
    background: 'rgba(15, 23, 42, 0.95)',
    border: '1px solid #c2410c',
    borderRadius: 6,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  };

  const headerStyle: CSSProperties = {
    fontSize: 20,
    fontWeight: 700,
    color: '#fdba74',
    paddingBottom: 8,
    borderBottom: '1px solid #c2410c',
  };

  const labelStyle: CSSProperties = {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#94a3b8',
  };

  const valueStyle: CSSProperties = {
    fontSize: 14,
    color: '#f1f5f9',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };

  if (!operationPlan) {
    return (
      <aside style={wrapper}>
        <div style={headerStyle}>Plan d'opération</div>
        <div style={{ ...valueStyle, color: '#94a3b8' }}>Aucun plan défini.</div>
        <AssetSections markers={markers} warLabels={warLabels} />
      </aside>
    );
  }

  return (
    <aside style={wrapper}>
      <div style={headerStyle}>
        {operationPlan.title || `Opération WAR ${operationPlan.warNumber}`}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={labelStyle}>Guerre</span>
        <span style={valueStyle}>WAR {operationPlan.warNumber}</span>
      </div>

      {operationPlan.date && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={labelStyle}>Date</span>
          <span style={valueStyle}>{operationPlan.date}</span>
        </div>
      )}

      {operationPlan.objectiveType !== 'defense' && operationPlan.objective && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          background: 'rgba(127, 29, 29, 0.25)',
          border: '1px solid #b91c1c',
          borderLeft: '4px solid #ef4444',
          borderRadius: 4,
          padding: '8px 10px',
        }}>
          <span style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6, color: '#fca5a5' }}>
            <GoldStarIcon kind="attack" />
            <span>{operationPlan.objectiveType === 'mixed' ? 'Objectif attaque' : 'Objectif'}</span>
          </span>
          <span style={{ ...valueStyle, fontSize: 15, fontWeight: 600, lineHeight: 1.4 }}>{operationPlan.objective}</span>
        </div>
      )}

      {operationPlan.objectiveType !== 'attack' && operationPlan.objectiveDefense && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          background: 'rgba(20, 83, 45, 0.25)',
          border: '1px solid #15803d',
          borderLeft: '4px solid #22c55e',
          borderRadius: 4,
          padding: '8px 10px',
        }}>
          <span style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6, color: '#86efac' }}>
            <GoldStarIcon kind="defense" />
            <span>{operationPlan.objectiveType === 'mixed' ? 'Objectif défense' : 'Objectif'}</span>
          </span>
          <span style={{ ...valueStyle, fontSize: 15, fontWeight: 600, lineHeight: 1.4 }}>{operationPlan.objectiveDefense}</span>
        </div>
      )}

      {/* Attack priorities */}
      {operationPlan.objectiveType !== 'defense' &&
        operationPlan.priorities.slice(0, ATTACK_SLOT_COUNT).some(Boolean) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={labelStyle}>
            {operationPlan.objectiveType === 'mixed' ? 'Priorités attaque' : 'Priorités'}
          </span>
          <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {operationPlan.priorities.slice(0, ATTACK_SLOT_COUNT).map((p, i) =>
              p ? (
                <li
                  key={`atk-${i}-${p}`}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: 'rgba(30, 41, 59, 0.6)', borderRadius: 4, padding: '4px 6px' }}
                >
                  <PriorityBadge index={i} />
                  <span style={{ ...valueStyle, flex: 1 }}>{p}</span>
                </li>
              ) : null
            )}
          </ol>
        </div>
      )}

      {/* Defense priorities */}
      {operationPlan.objectiveType !== 'attack' &&
        operationPlan.priorities.slice(DEFEND_SLOT_OFFSET, DEFEND_SLOT_OFFSET + DEFEND_SLOT_COUNT).some(Boolean) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={labelStyle}>
            {operationPlan.objectiveType === 'mixed' ? 'Priorités défense' : 'Priorités'}
          </span>
          <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {operationPlan.priorities.slice(DEFEND_SLOT_OFFSET, DEFEND_SLOT_OFFSET + DEFEND_SLOT_COUNT).map((p, i) =>
              p ? (
                <li
                  key={`def-${i}-${p}`}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: 'rgba(30, 41, 59, 0.6)', borderRadius: 4, padding: '4px 6px' }}
                >
                  <PriorityBadge index={DEFEND_SLOT_OFFSET + i} />
                  <span style={{ ...valueStyle, flex: 1 }}>{p}</span>
                </li>
              ) : null
            )}
          </ol>
        </div>
      )}

      <AssetSections markers={markers} warLabels={warLabels} />
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Asset list sections (one per op_* category)
// ---------------------------------------------------------------------------

const ALPHA = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

function AssetSections({
  markers,
  warLabels,
}: {
  markers: ReadonlyArray<MapObject>;
  warLabels: ReadonlyArray<ExportWarLabel>;
}): React.JSX.Element {
  const byKind = new Map<IconCategory, MapObject[]>();
  for (const k of OPERATION_SECTIONS) byKind.set(k, []);
  for (const m of markers) {
    const list = byKind.get(m.kind);
    if (list) list.push(m);
  }
  for (const list of byKind.values()) {
    list.sort((a, b) =>
      ALPHA.compare(
        a.metadata.note?.trim() || a.metadata.iconId,
        b.metadata.note?.trim() || b.metadata.iconId,
      ),
    );
  }

  return (
    <>
      {OPERATION_SECTIONS.map((kind) => {
        const items = byKind.get(kind) ?? [];
        if (items.length === 0) return null;
        return (
          <AssetSection
            key={kind}
            kind={kind}
            label={ICON_CATALOG[kind].label}
            items={items}
            warLabels={warLabels}
          />
        );
      })}
    </>
  );
}

function AssetSection({
  kind,
  label,
  items,
  warLabels,
}: {
  kind: IconCategory;
  label: string;
  items: ReadonlyArray<MapObject>;
  warLabels: ReadonlyArray<ExportWarLabel>;
}): React.JSX.Element {
  const headerStyle: CSSProperties = {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#cbd5e1',
    lineHeight: 1.15,
    paddingTop: 6,
    borderTop: '1px solid #334155',
  };
  const rowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '4px 6px',
    background: 'rgba(30, 41, 59, 0.6)',
    borderRadius: 4,
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={headerStyle}>
        <div>
          {label} <span style={{ color: '#94a3b8' }}>({items.length})</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {items.map((obj) => {
          const entry = getIconEntry(kind, obj.metadata.iconId);
          const region = findRegionLabel(obj.position.x, obj.position.y);
          const major = findNearestLabel(obj.position.x, obj.position.y, warLabels, 'major');
          const minor = findNearestLabel(obj.position.x, obj.position.y, warLabels, 'minor');
          const note = obj.metadata.note?.trim();
          return (
            <div key={obj.id} style={rowStyle}>
              <img
                src={entry.src}
                alt=""
                width={LIST_ICON_SIZE}
                height={LIST_ICON_SIZE}
                style={{ objectFit: 'contain', flexShrink: 0, marginTop: 1 }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <div
                  style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}
                >
                  {note || entry.label}
                </div>
                <div style={{ color: '#cbd5e1', fontSize: 11, lineHeight: 1.25 }}>
                  {region}
                  {major ? ` / ${major}` : ''}
                </div>
                {minor && (
                  <div style={{ color: '#94a3b8', fontSize: 11, lineHeight: 1.25 }}>
                    {minor}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Decorative SVG icons (gold star + numbered priority badge)
// ---------------------------------------------------------------------------

function GoldStarIcon({ kind }: { kind: 'attack' | 'defense' }): React.JSX.Element {
  const entry = kind === 'attack'
    ? getIconEntry('op_attack', 'OPERATION_ATTACK_MAIN_OBJECTIVE')
    : getIconEntry('op_defend', 'OPERATION_DEFEND_MAIN_OBJECTIVE');
  return (
    <img src={entry.src} alt="" width={16} height={16} style={{ objectFit: 'contain', flexShrink: 0 }} />
  );
}

function PriorityBadge({ index }: { index: number }): React.JSX.Element {
  const slot = PRIORITY_SLOTS[index];
  if (!slot) return <span style={{ width: 26, height: 26, flexShrink: 0 }} />;
  const entry = getIconEntry(slot.kind, slot.iconId);
  return (
    <img
      src={entry.src}
      alt={entry.label}
      width={26}
      height={26}
      style={{ flexShrink: 0, objectFit: 'contain' }}
    />
  );
}
